import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { collection, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { FiLogOut, FiPlus, FiSearch } from 'react-icons/fi';
import { theme } from '../../../theme';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Card, CardHeader, CardTitle, CardBody } from '../../../components/ui/Card';
import { Modal } from '../../../components/ui/Modal';
import { Table } from '../../../components/ui/Table';
import { db } from '../../../firebase/config';
import { CompanyData } from '../../../firebase/auth';
import {
  BranchPinLookupData,
  findBranchByCodeViaQuery,
  verifyBranchPin,
  verifySecurityCode,
} from '../service/branchPinLookupService';
import { playNotificationBeep } from '../../../utils/notificationSound';
import {
  Complain,
  ComplainStatus,
  COMPLAIN_STATUSES,
  createComplain,
  subscribeComplainsForBranch,
  updateComplainStatus,
} from '../service/complainService';
import { useClientPagination } from '../../../hooks/useClientPagination';

const PageShell = styled.div`
  min-height: 100vh;
  background-color: ${theme.colors.light.background.secondary};
  padding: ${theme.spacing[6]} ${theme.spacing[4]};

  @media (min-width: ${theme.breakpoints.lg}) {
    padding: ${theme.spacing[8]} ${theme.spacing[6]};
  }
`;

const ContentInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const BrandBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};
  padding-bottom: ${theme.spacing[6]};
  border-bottom: 1px solid ${theme.colors.light.border.light};
`;

const BrandBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  min-width: 0;
`;

const ToolbarPublic = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[4]};
`;

const FilterCheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const FilterCheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.primary};
  cursor: pointer;
`;

const ChatFab = styled.button`
  position: fixed;
  right: ${theme.spacing[6]};
  bottom: ${theme.spacing[6]};
  z-index: 1000;
  width: 56px;
  height: 56px;
  border-radius: ${theme.borderRadius.full};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.white};
  background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.primary[700]});
  box-shadow: ${theme.shadows.lg};

  &:hover {
    filter: brightness(1.05);
  }
`;

const BrandLogo = styled.div<{ $hasImage?: boolean }>`
  width: 56px;
  height: 56px;
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  flex-shrink: 0;
  background: ${({ $hasImage }) =>
    $hasImage ? 'transparent' : theme.colors.light.background.tertiary};
  border: 1px solid ${theme.colors.light.border.light};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.light.text.secondary};
  font-weight: ${theme.typography.fontWeight.bold};
  box-shadow: none;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const BrandText = styled.div`
  min-width: 0;
`;

const BrandTitle = styled.h1`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin: 0 0 ${theme.spacing[1]} 0;
`;

const BrandMeta = styled.p`
  margin: 0;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
  line-height: 1.5;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};
`;

const PageTitle = styled.h2`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin: 0;
`;

const PageHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};
`;

const PinIntro = styled.p`
  margin: 0 0 ${theme.spacing[6]} 0;
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.light.text.secondary};
  max-width: 520px;
`;

const ErrorBox = styled.div`
  padding: ${theme.spacing[4]};
  background: ${theme.colors.accent.danger}12;
  border: 1px solid ${theme.colors.accent.danger}35;
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.light.text.primary};
  margin-bottom: ${theme.spacing[4]};
  font-size: ${theme.typography.fontSize.sm};
`;

const FormGroup = styled.div`
  margin-bottom: ${theme.spacing[4]};
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.light.text.primary};
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.light.border.medium};
  border-radius: ${theme.borderRadius.md};
  resize: vertical;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &::placeholder {
    color: ${theme.colors.light.text.muted};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

const Label = styled.label`
  display: block;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.light.text.secondary};
  margin-bottom: ${theme.spacing[2]};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  color: ${theme.colors.light.text.secondary};
`;

const EmptyStateIcon = styled.div`
  font-size: 48px;
  margin-bottom: ${theme.spacing[4]};
  opacity: 0.5;
`;

const EmptyStateText = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  margin: 0;
`;

const DescCell = styled.span`
  display: inline-block;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatusStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${theme.spacing[1]};
`;

const RejectionReasonText = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.secondary};
  max-width: 260px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const Badge = styled.span<{
  variant?: 'open' | 'progress' | 'resolved' | 'done' | 'closed' | 'completed' | 'rejected';
}>`
  display: inline-block;
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  background-color: ${({ variant = 'open' }) => {
    switch (variant) {
      case 'progress':
        return `${theme.colors.primary[500]}20`;
      case 'resolved':
        return `${theme.colors.secondary[400]}25`;
      case 'done':
        return `${theme.colors.accent.success}20`;
      case 'completed':
        return `${theme.colors.accent.success}22`;
      case 'rejected':
        return `${theme.colors.accent.danger}18`;
      case 'closed':
        return `${theme.colors.light.background.tertiary}`;
      default:
        return `${theme.colors.secondary[200]}`;
    }
  }};
  color: ${({ variant = 'open' }) => {
    switch (variant) {
      case 'progress':
        return theme.colors.primary[700];
      case 'resolved':
        return theme.colors.secondary[700];
      case 'done':
        return theme.colors.accent.success;
      case 'completed':
        return theme.colors.accent.success;
      case 'rejected':
        return theme.colors.accent.danger;
      case 'closed':
        return theme.colors.light.text.secondary;
      default:
        return theme.colors.light.text.primary;
    }
  }};
`;

function formatStatus(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function statusVariant(
  s: string
): 'open' | 'progress' | 'resolved' | 'done' | 'closed' | 'completed' | 'rejected' {
  if (s === 'in_progress') return 'progress';
  if (s === 'resolved') return 'resolved';
  if (s === 'completed') return 'completed';
  if (s === 'rejected') return 'rejected';
  if (s === 'closed') return 'closed';
  return 'open';
}

const STORAGE_PUBLIC_COMPLAIN_CODE = 'guard_portal_public_complain_code_v1';

function allStatusesSelected(): Record<ComplainStatus, boolean> {
  return COMPLAIN_STATUSES.reduce(
    (acc, s) => {
      acc[s] = true;
      return acc;
    },
    {} as Record<ComplainStatus, boolean>
  );
}

function rowMatchesSearchPublic(r: Complain, q: string): boolean {
  const t = q.trim().toLowerCase();
  if (!t) return true;
  return [r.id, r.type, r.description]
    .filter((x) => x != null && String(x).trim() !== '')
    .some((x) => String(x).toLowerCase().includes(t));
}

function formatFilterStatusLabel(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const PublicComplainPage: React.FC = () => {
  const [code, setCode] = useState('');
  const [pinError, setPinError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);

  const [lookup, setLookup] = useState<BranchPinLookupData | null>(null);
  const [company, setCompany] = useState<CompanyData | null>(null);

  const [rows, setRows] = useState<Complain[]>([]);
  const [loadingList, setLoadingList] = useState(false);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [type, setType] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionBusyId, setActionBusyId] = useState<string | null>(null);
  const [rejectModalRow, setRejectModalRow] = useState<Complain | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [rememberCode, setRememberCode] = useState(true);
  const [search, setSearch] = useState('');
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [statusFilters, setStatusFilters] = useState<Record<ComplainStatus, boolean>>(() =>
    allStatusesSelected()
  );
  const [flashComplainId, setFlashComplainId] = useState<string | null>(null);

  const locked = !lookup;

  const displayRows = useMemo(() => {
    return rows.filter((r) => statusFilters[r.status] && rowMatchesSearchPublic(r, search));
  }, [rows, statusFilters, search]);

  const activeFilterCount = useMemo(
    () => COMPLAIN_STATUSES.filter((s) => !statusFilters[s]).length,
    [statusFilters]
  );

  const {
    pagedItems: pagedPublicComplains,
    page: publicComplainPage,
    setPage: setPublicComplainPage,
    totalPages: publicComplainTotalPages,
    usePaging: publicComplainUsePaging,
    rangeStart: publicComplainRangeStart,
    rangeEnd: publicComplainRangeEnd,
    totalItems: publicComplainTotalItems,
  } = useClientPagination(displayRows, displayRows.length, search, activeFilterCount);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_PUBLIC_COMPLAIN_CODE);
      if (saved) setCode(saved);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!lookup?.companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'companies', lookup.companyId));
        if (!cancelled) {
          setCompany(snap.exists() ? ({ id: snap.id, ...(snap.data() as any) } as CompanyData) : null);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) setCompany(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lookup?.companyId]);

  useEffect(() => {
    if (!lookup) return;
    setLoadingList(true);
    const unsub = subscribeComplainsForBranch(
      lookup.companyId,
      lookup.bankId,
      lookup.branchId,
      (list) => {
        setRows(list);
        setLoadingList(false);
      },
      (err) => {
        console.error(err);
        setRows([]);
        setLoadingList(false);
      }
    );
    return unsub;
  }, [lookup?.companyId, lookup?.bankId, lookup?.branchId]);

  useEffect(() => {
    if (!lookup?.companyId || !lookup.bankId || !lookup.branchId) return undefined;
    const col = collection(
      db,
      'bankClient',
      lookup.companyId,
      'banks',
      lookup.bankId,
      'branches',
      lookup.branchId,
      'notifications'
    );
    let initial = true;
    const unsub = onSnapshot(
      col,
      (snap) => {
        if (initial) {
          initial = false;
          return;
        }
        snap.docChanges().forEach((ch) => {
          if (ch.type !== 'added') return;
          const d = ch.doc.data() as {
            type?: string;
            action?: string;
            actor?: string;
            title?: string;
            body?: string;
            complainId?: string;
          };
          if (d.type !== 'complain' || d.action !== 'updated' || d.actor !== 'company') return;
          playNotificationBeep();
          const complainId = d.complainId || '';
          toast.custom(
            (t) => (
              <button
                type="button"
                onClick={() => {
                  toast.dismiss(t.id);
                  if (complainId) setFlashComplainId(complainId);
                }}
                style={{
                  margin: 0,
                  padding: `${theme.spacing[3]} ${theme.spacing[4]}`,
                  width: '100%',
                  maxWidth: 400,
                  textAlign: 'left',
                  cursor: 'pointer',
                  borderRadius: theme.borderRadius.md,
                  border: `1px solid ${theme.colors.light.border.medium}`,
                  background: theme.colors.white,
                  boxShadow: theme.shadows.lg,
                  fontFamily: theme.typography.fontFamily.primary,
                }}
              >
                <div
                  style={{
                    fontWeight: theme.typography.fontWeight.semibold,
                    marginBottom: theme.spacing[1],
                    color: '#000000',
                  }}
                >
                  {d.title || 'Status update'}
                </div>
                {d.body ? (
                  <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.secondary }}>
                    {d.body}
                  </div>
                ) : null}
                <div
                  style={{
                    marginTop: theme.spacing[2],
                    fontSize: theme.typography.fontSize.xs,
                    color: theme.colors.primary[600],
                  }}
                >
                  Tap to show in list
                </div>
              </button>
            ),
            { duration: 12000 }
          );
        });
      },
      (err) => console.error('Branch notifications', err)
    );
    return unsub;
  }, [lookup?.companyId, lookup?.bankId, lookup?.branchId]);

  useEffect(() => {
    if (!flashComplainId) return undefined;
    const el = document.getElementById(`complain-row-${flashComplainId}`);
    requestAnimationFrame(() => {
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    const tid = window.setTimeout(() => setFlashComplainId(null), 5000);
    return () => window.clearTimeout(tid);
  }, [flashComplainId]);

  const onVerifyPin = async (e: React.FormEvent) => {
    e.preventDefault();
    setPinError(null);
    setCompany(null);
    setRows([]);

    try {
      setVerifying(true);
      const byPin = await verifyBranchPin(code);
      const bySecurity = byPin ? null : await verifySecurityCode(code);
      const byQuery = byPin || bySecurity ? null : await findBranchByCodeViaQuery(code);
      const ok = byPin ?? bySecurity ?? byQuery;
      if (!ok?.companyId || !ok?.bankId || !ok?.branchId) {
        setPinError('Invalid code. Please check your PIN / Security Code or contact support.');
        return;
      }
      setLookup(ok);
      try {
        if (rememberCode) localStorage.setItem(STORAGE_PUBLIC_COMPLAIN_CODE, code.trim());
        else localStorage.removeItem(STORAGE_PUBLIC_COMPLAIN_CODE);
      } catch {
        /* ignore */
      }
      toast.success('Code verified');
    } catch (err) {
      console.error(err);
      setPinError('Could not verify code. If this persists, contact support.');
    } finally {
      setVerifying(false);
    }
  };

  const submitComplain = async () => {
    if (!lookup) return;
    if (!type.trim() || !description.trim()) {
      toast.error('Please enter type and description');
      return;
    }
    try {
      setSubmitting(true);
      await createComplain({
        companyId: lookup.companyId,
        bankId: lookup.bankId,
        branchId: lookup.branchId,
        bankName: lookup.bankName,
        address: lookup.address,
        branchCode: lookup.branchCode,
        type: type.trim(),
        description: description.trim(),
      });
      toast.success('Complain submitted');
      setType('');
      setDescription('');
      setIsCreateOpen(false);
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkResolved = async (row: Complain) => {
    if (!lookup) return;
    try {
      setActionBusyId(row.id);
      await updateComplainStatus(lookup.companyId, lookup.bankId, lookup.branchId, row.id, 'resolved', {
        statusChangeActor: 'public',
      });
      toast.success('Marked as resolved');
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || 'Could not update status');
    } finally {
      setActionBusyId(null);
    }
  };

  const confirmRejectWithReason = async () => {
    if (!lookup || !rejectModalRow) return;
    const reason = rejectReason.trim();
    if (!reason) {
      toast.error('Please enter a rejection reason');
      return;
    }
    try {
      setRejectSubmitting(true);
      await updateComplainStatus(
        lookup.companyId,
        lookup.bankId,
        lookup.branchId,
        rejectModalRow.id,
        'rejected',
        { rejectionReason: reason, statusChangeActor: 'public' }
      );
      toast.success('Rejected');
      setRejectModalRow(null);
      setRejectReason('');
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || 'Could not update status');
    } finally {
      setRejectSubmitting(false);
    }
  };

  const companyTitle = company?.companyName || company?.name || 'Security Portal';
  const companyLogo = company?.companyImg || company?.logo || company?.logoUrl || company?.companyLogo;

  const handleLogout = () => {
    setLookup(null);
    setCompany(null);
    setRows([]);
  };

  const openChat = () => {
    const url = process.env.REACT_APP_PUBLIC_CHAT_URL?.trim();
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
    else window.location.href = 'mailto:?subject=Branch%20complain%20support';
  };

  if (locked) {
    return (
      <PageShell>
        <ContentInner>
          <BrandBar>
            <BrandBarLeft>
              <BrandLogo $hasImage={!!companyLogo}>
                {companyLogo ? <img src={companyLogo} alt={companyTitle} /> : '🛡️'}
              </BrandLogo>
              <BrandText>
                <BrandTitle>{companyTitle}</BrandTitle>
                <BrandMeta>Branch complains portal</BrandMeta>
              </BrandText>
            </BrandBarLeft>
          </BrandBar>

          <PageHeaderRow>
            <div>
              <PageTitle>Access</PageTitle>
              <PinIntro>
                Enter your branch PIN or Security Code to view complains and submit a new one.
              </PinIntro>
            </div>
          </PageHeaderRow>

          <Card>
            <CardHeader>
              <CardTitle>Verify branch</CardTitle>
            </CardHeader>
            <CardBody>
              <form onSubmit={onVerifyPin}>
                {pinError && <ErrorBox>{pinError}</ErrorBox>}
                <FormGroup>
                  <Input
                    label="Branch PIN / Security Code"
                    type="password"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="e.g. H58581160 or BNK-4584"
                    fullWidth
                    dark
                  />
                </FormGroup>
                <FormGroup style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2] }}>
                  <input
                    id="remember-branch-code"
                    type="checkbox"
                    checked={rememberCode}
                    onChange={(e) => setRememberCode(e.target.checked)}
                  />
                  <Label htmlFor="remember-branch-code" style={{ marginBottom: 0, cursor: 'pointer' }}>
                    Remember this code on this device
                  </Label>
                </FormGroup>
                <Button type="submit" disabled={verifying} loading={verifying}>
                  {verifying ? 'Verifying…' : 'Continue'}
                </Button>
                <p
                  style={{
                    marginTop: theme.spacing[4],
                    fontSize: theme.typography.fontSize.sm,
                    color: theme.colors.light.text.secondary,
                  }}
                >
                  Need help? Contact your security company.
                </p>
              </form>
            </CardBody>
          </Card>
          <ChatFab type="button" onClick={openChat} aria-label="Open chat" title="Chat">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
            </svg>
          </ChatFab>
        </ContentInner>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <ContentInner>
        <BrandBar>
          <BrandBarLeft>
            <BrandLogo $hasImage={!!companyLogo}>
              {companyLogo ? <img src={companyLogo} alt={companyTitle} /> : '🛡️'}
            </BrandLogo>
            <BrandText>
              <BrandTitle>{companyTitle}</BrandTitle>
              <BrandMeta>
                {lookup?.bankName ? `${lookup.bankName} — ` : ''}
                {lookup?.address || 'Branch'}
                {lookup?.branchCode != null && lookup.branchCode !== '' ? ` · Code ${lookup.branchCode}` : ''}
              </BrandMeta>
            </BrandText>
          </BrandBarLeft>
          <Button type="button" variant="secondary" onClick={handleLogout}>
            <FiLogOut size={18} style={{ marginRight: theme.spacing[2] }} />
            Sign out
          </Button>
        </BrandBar>

        <PageHeader>
          <PageTitle>Complains</PageTitle>
          <Button
            onClick={() => {
              setType('');
              setDescription('');
              setIsCreateOpen(true);
            }}
          >
            <FiPlus size={18} style={{ marginRight: theme.spacing[2] }} />
            Create complain
          </Button>
        </PageHeader>

        <Card>
          <CardHeader>
            <CardTitle>Your complains</CardTitle>
          </CardHeader>
          <CardBody>
            {!loadingList && rows.length > 0 ? (
              <ToolbarPublic>
                <div
                  style={{
                    flex: '1 1 200px',
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing[2],
                    color: theme.colors.light.text.secondary,
                  }}
                >
                  <FiSearch size={20} aria-hidden />
                  <Input
                    type="text"
                    placeholder="Search ID, type, description…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    fullWidth
                    dark
                  />
                </div>
                <Button type="button" variant="secondary" onClick={() => setFilterModalOpen(true)}>
                  <svg
                    width={18}
                    height={18}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                    style={{ marginRight: activeFilterCount > 0 ? theme.spacing[2] : 0 }}
                  >
                    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                  </svg>
                  Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
                </Button>
              </ToolbarPublic>
            ) : null}
            {loadingList ? (
              <EmptyState>
                <EmptyStateText>Loading…</EmptyStateText>
              </EmptyState>
            ) : rows.length === 0 ? (
              <EmptyState>
                <EmptyStateIcon>📋</EmptyStateIcon>
                <EmptyStateText>No complains yet. Use Create complain to submit one.</EmptyStateText>
              </EmptyState>
            ) : displayRows.length === 0 ? (
              <EmptyState>
                <EmptyStateIcon>🔎</EmptyStateIcon>
                <EmptyStateText>No complains match your search or filters.</EmptyStateText>
              </EmptyState>
            ) : (
              <>
              <Table>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Complain ID</Table.HeaderCell>
                    <Table.HeaderCell>Type</Table.HeaderCell>
                    <Table.HeaderCell>Description</Table.HeaderCell>
                    <Table.HeaderCell>Status</Table.HeaderCell>
                    <Table.HeaderCell>Respond</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {pagedPublicComplains.map((r) => (
                    <Table.Row
                      key={r.id}
                      id={`complain-row-${r.id}`}
                      style={
                        flashComplainId === r.id
                          ? {
                              outline: `2px solid ${theme.colors.primary[500]}`,
                              outlineOffset: -2,
                              background: `${theme.colors.primary[500]}12`,
                            }
                          : undefined
                      }
                    >
                      <Table.Cell style={{ fontFamily: 'monospace', fontSize: theme.typography.fontSize.sm }}>
                        {r.id}
                      </Table.Cell>
                      <Table.Cell>{r.type}</Table.Cell>
                      <Table.Cell>
                        <DescCell title={r.description}>{r.description}</DescCell>
                      </Table.Cell>
                      <Table.Cell>
                        <StatusStack>
                          <Badge variant={statusVariant(r.status)}>{formatStatus(r.status)}</Badge>
                          {r.status === 'rejected' && r.rejectionReason ? (
                            <RejectionReasonText>{r.rejectionReason}</RejectionReasonText>
                          ) : null}
                        </StatusStack>
                      </Table.Cell>
                      <Table.Cell>
                        {r.status === 'completed' ? (
                          <Select
                            aria-label="Branch response"
                            size="sm"
                            fullWidth
                            dark
                            disabled={actionBusyId === r.id}
                            value=""
                            onChange={(e) => {
                              const v = e.target.value as '' | 'resolved' | 'rejected';
                              if (v === 'resolved') void handleMarkResolved(r);
                              else if (v === 'rejected') {
                                setRejectReason('');
                                setRejectModalRow(r);
                              }
                            }}
                          >
                            <option value="">Respond…</option>
                            <option value="resolved">Resolved</option>
                            <option value="rejected">Rejected</option>
                          </Select>
                        ) : (
                          <span
                            style={{
                              color: theme.colors.light.text.muted,
                              fontSize: theme.typography.fontSize.sm,
                            }}
                          >
                            —
                          </span>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
              {publicComplainUsePaging ? (
                <div
                  style={{
                    marginTop: theme.spacing[4],
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: theme.spacing[3],
                  }}
                >
                  <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.secondary }}>
                    Showing {publicComplainRangeStart}–{publicComplainRangeEnd} of {publicComplainTotalItems}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2] }}>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={publicComplainPage <= 1}
                      onClick={() => setPublicComplainPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </Button>
                    <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.primary }}>
                      Page {publicComplainPage} of {publicComplainTotalPages}
                    </span>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={publicComplainPage >= publicComplainTotalPages}
                      onClick={() => setPublicComplainPage((p) => Math.min(publicComplainTotalPages, p + 1))}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              ) : null}
              </>
            )}
          </CardBody>
        </Card>

        <Modal
          isOpen={!!rejectModalRow}
          onClose={() => {
            if (rejectSubmitting) return;
            setRejectModalRow(null);
            setRejectReason('');
          }}
          title="Reject complain"
          size="md"
          footer={
            <>
              <Button
                variant="secondary"
                type="button"
                onClick={() => {
                  if (rejectSubmitting) return;
                  setRejectModalRow(null);
                  setRejectReason('');
                }}
                disabled={rejectSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="danger"
                onClick={() => void confirmRejectWithReason()}
                loading={rejectSubmitting}
                disabled={rejectSubmitting || !rejectReason.trim()}
              >
                Submit rejection
              </Button>
            </>
          }
        >
          <FormGroup>
            <Label htmlFor="reject-reason">Reason for rejection</Label>
            <TextArea
              id="reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Explain why this complain is rejected…"
            />
          </FormGroup>
        </Modal>

        <Modal
          isOpen={isCreateOpen}
          onClose={() => !submitting && setIsCreateOpen(false)}
          title="Create complain"
          size="md"
          footer={
            <>
              <Button variant="secondary" onClick={() => setIsCreateOpen(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button onClick={submitComplain} loading={submitting} disabled={submitting} type="button">
                Submit
              </Button>
            </>
          }
        >
          <FormGroup>
            <Input
              label="Type"
              name="type"
              value={type}
              onChange={(e) => setType(e.target.value)}
              placeholder="e.g. Deployment, Equipment"
              fullWidth
              dark
            />
          </FormGroup>
          <div>
            <Label htmlFor="complain-description">Description</Label>
            <TextArea
              id="complain-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the issue…"
            />
          </div>
        </Modal>

        <Modal
          isOpen={filterModalOpen}
          onClose={() => setFilterModalOpen(false)}
          title="Filter by status"
          size="sm"
          footer={
            <>
              <Button type="button" variant="secondary" onClick={() => setStatusFilters(allStatusesSelected())}>
                Show all statuses
              </Button>
              <Button type="button" onClick={() => setFilterModalOpen(false)}>
                Done
              </Button>
            </>
          }
        >
          <FilterCheckboxList>
            {COMPLAIN_STATUSES.map((s) => (
              <FilterCheckboxRow key={s}>
                <input
                  type="checkbox"
                  checked={statusFilters[s]}
                  onChange={() =>
                    setStatusFilters((prev) => ({
                      ...prev,
                      [s]: !prev[s],
                    }))
                  }
                />
                <span>{formatFilterStatusLabel(s)}</span>
              </FilterCheckboxRow>
            ))}
          </FilterCheckboxList>
        </Modal>

        <ChatFab type="button" onClick={openChat} aria-label="Open chat" title="Chat">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
          </svg>
        </ChatFab>
      </ContentInner>
    </PageShell>
  );
};

export default PublicComplainPage;
